import ApiService from '@core/api/services/ApiService';
import { imageUrl } from '@helpers/image';
import { MediaComponent } from '@helpers/mediaComponent';
import { SystemIcon, getSvgFromSystem } from '@helpers/systemIcons';
import { withMotion } from '@hoc/withMotion';
import { withQueryParams } from '@hoc/withQueryParams';
import SvgButtonFill from '@img/icons/button_end_inner.svg';
import CloseIcon from '@img/icons/close.svg';
import MenuIcon from '@img/icons/hamburger.svg';
import Search from '@img/icons/search.svg';
import { AnimatePresence } from 'framer-motion';
import * as React from 'react';
import { FocusRing } from 'react-aria';
import { useLockBodyScroll, useMeasure } from 'react-use';
import DesktopNavigation from './DesktopNavigation/DesktopNavigation';
import S from './Header.styles';
import MobileNavigation from './MobileNavigation/MobileNavigation';
import SearchModal from './SearchModal/SearchModal';
export const SearchBar = ({ isSearching, performSearch, search, setSearch, setSearchResponse }) => {
    return (React.createElement(S.SearchBar, { "data-disabled": isSearching },
        React.createElement(S.SearchBarInput, { value: search, onChange: (e) => setSearch(e.currentTarget.value), onKeyDown: (e) => e.key === 'Enter' && performSearch(), placeholder: "What are you looking for?", type: "text" }),
        React.createElement(S.SearchBarEndContent, null,
            React.createElement(AnimatePresence, { exitBeforeEnter: true }, search !== '' && (React.createElement(S.SearchBarClear, { onClick: () => (setSearch(''),
                    setSearchResponse({
                        news: { items: [], canLoadMore: false, totalResults: 0 },
                        events: { items: [], canLoadMore: false, totalResults: 0 },
                        pages: { items: [], canLoadMore: false, totalResults: 0 },
                    })) }, "Clear"))),
            React.createElement(Search, { onClick: () => performSearch() }))));
};
const Header = (props) => {
    const [showSearch, setShowSearch] = React.useState(false);
    const [showMenu, setShowMenu] = React.useState(false);
    const [showOverlay, setShowOverlay] = React.useState(false);
    const [hasScrolled, setHasScrolled] = React.useState(false);
    const [ref, bounds] = useMeasure();
    const [search, setSearch] = React.useState('');
    const [isSearching, setIsSearching] = React.useState(false);
    const [searchResponse, setSearchResponse] = React.useState();
    const [newsPageNumber, setNewsPageNumber] = React.useState(2);
    const [eventsPageNumber, setEventsPageNumber] = React.useState(2);
    const [pagesPageNumber, setPagesPageNumber] = React.useState(2);
    useLockBodyScroll(showSearch);
    const performSearch = async () => {
        setIsSearching(true);
        const searchResponse = await ApiService.request({
            method: 'GET',
            slug: 'perform',
            controller: 'search',
            params: {
                newsPage: newsPageNumber,
                eventsPage: eventsPageNumber,
                pagesPage: pagesPageNumber,
                query: search,
            },
        });
        setSearchResponse(searchResponse);
        setIsSearching(false);
    };
    const nextPage = (tab) => {
        switch (tab) {
            case 'news':
                setNewsPageNumber(newsPageNumber + 1);
                break;
            case 'events':
                setEventsPageNumber(eventsPageNumber + 1);
                break;
            case 'pages':
                setPagesPageNumber(pagesPageNumber + 1);
                break;
            default:
                break;
        }
        performSearch();
    };
    const mainMenuHeight = React.useMemo(() => bounds.height, [bounds]);
    const handleScroll = () => {
        const position = window.scrollY;
        position > 0 ? setHasScrolled(true) : setHasScrolled(false);
    };
    React.useEffect(() => {
        handleScroll();
    }, []);
    React.useEffect(() => {
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [hasScrolled]);
    if (!props.logo) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(S.GlobalStyles, null),
        React.createElement(S.Container, { isEditMode: props.pageContext?.isEditMode ?? false, "data-dark-bg": !!props.isDarkBg },
            React.createElement(S.Overlay, { "data-action-open": showMenu || showOverlay },
                React.createElement(S.ShapeBottomRight, null)),
            React.createElement(S.MainContainer, { ref: ref, "data-show-bg": hasScrolled },
                React.createElement(S.Inner, null,
                    React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                        React.createElement(S.LogoWrapper, { href: "/" },
                            React.createElement(S.Logo, { src: imageUrl(props.logo, { width: 276, rmode: 'pad' }), alt: props.logo.altText ? props.logo.altText : 'Company Logo', loading: "eager" }))),
                    React.createElement(AnimatePresence, null, showSearch ? (React.createElement(S.SearchWrapper, { key: "HeaderSearch" },
                        React.createElement(MediaComponent, { desktop: true },
                            React.createElement(SearchBar, { isSearching: isSearching, performSearch: performSearch, search: search, setSearch: setSearch, setSearchResponse: (searchRes) => setSearchResponse(searchRes) })),
                        React.createElement(S.SearchClose, null,
                            React.createElement(MediaComponent, { desktop: true },
                                React.createElement(S.SearchButton, { branding: "peach", styleType: "solid", onClick: () => setShowSearch(false), "aria-label": "Close Search", addon: getSvgFromSystem(SystemIcon.Close), addonPosition: "left", title: props.translations['hyve.global.search-btn.close'] ?? 'Close' })),
                            React.createElement(MediaComponent, { mobile: true },
                                React.createElement(S.CloseButton, { onClick: () => {
                                        setShowSearch(false);
                                        setShowMenu(false);
                                    }, "aria-label": "Close Menu" },
                                    React.createElement(S.ButtonBg, null),
                                    React.createElement(CloseIcon, null)))),
                        React.createElement(MediaComponent, { desktop: true },
                            React.createElement(AnimatePresence, null, search === '' && props.popularSearches && (React.createElement(S.PopularSearches, null,
                                React.createElement("span", null,
                                    props.translations['hyve.global.search.popular-searches'],
                                    ":"),
                                React.createElement(S.PopularSearchList, null, props.popularSearches.map((popularSearch, i) => (React.createElement(S.PopularSearch, { href: popularSearch.url, key: `Header_PopularSearches_${i}` }, popularSearch.text)))))))))) : (React.createElement(S.ActionWrapper, { key: "HeaderNavigation", "data-action-open": showSearch || showMenu },
                        React.createElement(S.NavStart, null,
                            React.createElement(SvgButtonFill, null)),
                        React.createElement(DesktopNavigation, { dropdownOpen: (e) => setShowOverlay(e), links: props.links }),
                        React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                            React.createElement(S.SearchButton, { branding: "peach", styleType: "solid", onClick: () => setShowSearch(true), "aria-label": "Show Search", addon: getSvgFromSystem(SystemIcon.Search), addonPosition: "left", title: props.translations['hyve.global.search-btn'] ?? 'Search' })),
                        React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                            React.createElement(S.MobileSearch, { onClick: () => {
                                    setShowSearch(true);
                                    setShowMenu(false);
                                }, "aria-label": "Open Search" },
                                React.createElement(S.ButtonBg, null),
                                getSvgFromSystem(SystemIcon.Search))),
                        React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                            React.createElement(S.MenuButton, { onClick: () => {
                                    setShowMenu(true);
                                }, "aria-label": "Open Menu" },
                                React.createElement(S.ButtonBg, null),
                                React.createElement(MenuIcon, null))),
                        React.createElement(FocusRing, { focusRingClass: "focus-ring" },
                            React.createElement(S.CloseButton, { onClick: () => {
                                    setShowSearch(false);
                                    setShowMenu(false);
                                }, "aria-label": "Close Menu" },
                                React.createElement(S.ButtonBg, null),
                                React.createElement(CloseIcon, null))),
                        React.createElement(S.NavEnd, null,
                            React.createElement(SvgButtonFill, null))))))),
            React.createElement(MobileNavigation, { links: props.links, isOpen: showMenu }),
            React.createElement(AnimatePresence, null, showSearch && (React.createElement(S.SearchModalWrapper, { mainMenuHeight: mainMenuHeight, key: `HeaderSearchModal` },
                React.createElement(SearchModal, { ...props.searchModal, search: search, setSearch: setSearch, isSearching: isSearching, translations: props.translations, searchResponse: searchResponse, nextPage: nextPage, performSearch: performSearch, popularSearches: props.popularSearches, setSearchResponse: (searchRes) => setSearchResponse(searchRes) })))))));
};
export default withQueryParams(withMotion(Header));
